import { Link } from 'react-router-dom';
import { Card } from '../components/ui.js';
import { useSetupState } from '../lib/useSetupState.js';

interface Step {
  label: string;
  hint: string;
  to: string;
  cta: string;
  done: boolean;
}

/** First-run activation nudge on the dashboard: get a statement in, set your real
 *  rates so owed is defensible, then review. Derived from existing data — disappears
 *  once the account is uploading + has rates set. */
export function OnboardingChecklist() {
  const { uploaded, rated, loaded } = useSetupState();

  if (!loaded) return null;
  if (uploaded && rated) return null; // fully activated → hide

  const steps: Step[] = [
    {
      label: 'Upload your first commission statement',
      hint: 'CSV / XLSX from any carrier we’ve set up for you — we score it in seconds.',
      to: '/files',
      cta: 'Upload',
      done: uploaded,
    },
    {
      label: 'Set your contracted rates',
      hint: 'So “commission owed” is your real rate vs. what the carrier paid — audit-grade, not an estimate.',
      to: '/rates',
      cta: 'Set rates',
      done: rated,
    },
    {
      label: 'Review your book & what you’re owed',
      hint: 'Member health, attrition, chargebacks, and recoverable underpayments.',
      to: '/audit',
      cta: 'Open audit',
      done: uploaded && rated,
    },
  ];
  const completed = steps.filter((s) => s.done).length;

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white dark:border-emerald-900/40 dark:from-emerald-950/20">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold">Get set up</h2>
        <span className="text-xs font-medium text-neutral-500">{completed} of 3 done</span>
      </div>
      <ol className="space-y-2.5">
        {steps.map((s, i) => (
          <li key={s.to} className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                s.done
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-neutral-500 ring-1 ring-neutral-300 dark:bg-neutral-800'
              }`}
            >
              {s.done ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                i + 1
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div
                className={`text-sm font-medium ${s.done ? 'text-neutral-400 line-through' : 'text-neutral-800 dark:text-neutral-100'}`}
              >
                {s.label}
              </div>
              {!s.done && <div className="mt-0.5 text-xs text-neutral-500">{s.hint}</div>}
            </div>
            {!s.done && (
              <Link
                to={s.to}
                className="shrink-0 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-700"
              >
                {s.cta}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}
